import React, { useState, useEffect } from 'react';
import { useGlobalConfig } from '@/context/ConfigContext';
import { useShareViewOptions } from '@/contexts/ShareViewOptionsContext';
import { useTranslate } from '@/utils/translate';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Globe, Trash2, Terminal, Copy, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

interface UnifiedSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const UnifiedSettingsModal: React.FC<UnifiedSettingsModalProps> = ({ open, onOpenChange }) => {
  const { 
    language,
    setLanguage,
    openaiApiKey,
    setOpenaiApiKey,
    loggingEnabled,
    setLoggingEnabled
  } = useGlobalConfig();
  
  const { 
    shareViewOptions, 
    updateShareViewOption, 
    applyPendingChanges,
    generateShareUrl, 
    generateEmbedCode,
    setShareViewOptions,
    setIsSharePanelOpen
  } = useShareViewOptions();
  
  const [apiKeyInput, setApiKeyInput] = useState(openaiApiKey || '');
  const [embedWidth, setEmbedWidth] = useState('100');
  const [embedHeight, setEmbedHeight] = useState('600');
  const t = useTranslate();
  
  // Check if we're in development mode
  const isDevelopment = process.env.NODE_ENV === 'development';

  // Responsive full-screen behavior: small width or short height
  const [isFullScreen, setIsFullScreen] = useState(false);
  useEffect(() => {
    const compute = () => {
      if (typeof window === 'undefined') return;
      const smallWidth = window.innerWidth <= 640;      // ~sm breakpoint
      const shortHeight = window.innerHeight <= 700;    // smaller laptops
      setIsFullScreen(smallWidth || shortHeight);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  // Update input when openaiApiKey changes (e.g., on initial load)
  useEffect(() => {
    setApiKeyInput(openaiApiKey || '');
  }, [openaiApiKey]);
  
  // Auto-save API key when it changes
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (apiKeyInput !== openaiApiKey) {
        await setOpenaiApiKey(apiKeyInput.trim() || null);
      }
    }, 500); // Debounce for 500ms
    
    return () => clearTimeout(timeoutId);
  }, [apiKeyInput, openaiApiKey, setOpenaiApiKey]);
  
  const handleClearApiKey = async () => {
    setApiKeyInput('');
    await setOpenaiApiKey(null);
  };
  
  const handleLanguageChange = (value: string) => {
    setLanguage(value);
  };

  const shareUrl = generateShareUrl();
  const [widthUnit, setWidthUnit] = useState<'px' | '%'>('%');
  const [lastPxWidth, setLastPxWidth] = useState<string>('800');
  const handleWidthUnitChange = (v: 'px' | '%') => {
    if (v === '%') {
      // Store current px width and switch to 100%
      if (widthUnit === 'px') {
        setLastPxWidth(embedWidth && !isNaN(Number(embedWidth)) ? String(parseInt(embedWidth)) : '800');
      }
      setEmbedWidth('100');
    } else {
      // Restore last px width when switching back
      setEmbedWidth(lastPxWidth || '800');
    }
    setWidthUnit(v);
  };
  const [heightUnit, setHeightUnit] = useState<'px' | '%'>('px');
  const [lastPxHeight, setLastPxHeight] = useState<string>('600');
  const handleHeightUnitChange = (v: 'px' | '%') => {
    if (v === '%') {
      if (heightUnit === 'px') {
        setLastPxHeight(embedHeight && !isNaN(Number(embedHeight)) ? String(parseInt(embedHeight)) : '600');
      }
      setEmbedHeight('100');
    } else {
      setEmbedHeight(lastPxHeight || '600');
    }
    setHeightUnit(v);
  };
  const embedWidthValue = widthUnit === '%' ? (embedWidth && !isNaN(Number(embedWidth)) ? `${parseInt(embedWidth)}%` : '100%') : (parseInt(embedWidth) || 800);
  const embedHeightValue = heightUnit === '%' ? (embedHeight && !isNaN(Number(embedHeight)) ? `${parseInt(embedHeight)}%` : '100%') : (parseInt(embedHeight) || 600);
  const embedCode = generateEmbedCode(embedWidthValue as any, embedHeightValue);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success(t('sharePanel.url.copiedSuccess'));
    } catch {
      toast.error(t('sharePanel.url.copyError'));
    }
  };

  const handleCopyEmbedCode = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      toast.success(t('sharePanel.embed.copiedSuccess'));
    } catch {
      toast.error(t('sharePanel.embed.copyError'));
    }
  };

  const handleResetViewOptions = () => {
    // Reset all view-related options in a single batch to avoid race conditions with deferred layout updates
    // Preserve admin and lang values from current state
    setShareViewOptions({
      ...shareViewOptions,
      layout: 'default',
      funcControls: true,
      fullscreen: false,
      tools: true,
      zoom: true,
      showUnits: true,
      header: true,
    });
  };

  const handleOpenChange = (newOpen: boolean) => {
    // Update SharePanel state
    setIsSharePanelOpen(newOpen);
    
    if (!newOpen) {
      // When closing the modal, apply any pending admin/layout changes
      applyPendingChanges();
      // Only reset Share tab specific options (embed dimensions), not View tab options
      setEmbedWidth(widthUnit === '%' ? '100' : '800');
      setEmbedHeight('600');
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent 
        className={
          isFullScreen
            ? 'left-0 top-0 translate-x-0 translate-y-0 w-screen h-screen max-w-none rounded-none m-0 p-4 overflow-y-auto flex flex-col'
            : 'sm:max-w-[700px] max-h-[90vh] overflow-y-auto flex flex-col'
        }
        onOpenAutoFocus={() => setIsSharePanelOpen(true)}
      >
        <DialogHeader className="pb-4 flex-shrink-0">
          <DialogTitle>{t('unifiedSettingsModal.title')}</DialogTitle>
          <DialogDescription>
            {t('unifiedSettingsModal.description')}
          </DialogDescription>
        </DialogHeader>
        
        <Tabs defaultValue="general" className="mt-2 flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-3 flex-shrink-0">
            <TabsTrigger value="general">{t('unifiedSettingsModal.tabs.general')}</TabsTrigger>
            <TabsTrigger value="view">{t('unifiedSettingsModal.tabs.view')}</TabsTrigger>
            <TabsTrigger value="share">{t('unifiedSettingsModal.tabs.share')}</TabsTrigger>
          </TabsList>
          
          {/* General Tab */}
          <TabsContent value="general" className="space-y-6 py-4 flex-1 overflow-y-auto">
            {/* Language Selection */}
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-medium">{t('unifiedSettingsModal.general.languageTitle')}</h3>
                <p className="text-xs text-muted-foreground">
                  {t('unifiedSettingsModal.general.languageDescription')}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="language" className="flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  <span>{t('configModal.general.languageLabel')}</span>
                </Label>
                <Select value={language} onValueChange={handleLanguageChange}>
                  <SelectTrigger id="language">
                    <SelectValue placeholder={t('configModal.general.languagePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="de">Deutsch</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            {/* OpenAI API Settings */}
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-medium">{t('unifiedSettingsModal.general.openaiTitle')}</h3>
                <p className="text-xs text-muted-foreground">
                  {t('configModal.openai.description')}
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label htmlFor="api-key" className="mb-1 block">
                      {t('configModal.openai.apiKeyLabel')}
                    </Label>
                    <Input
                      id="api-key"
                      type="password"
                      placeholder={t('configModal.openai.apiKeyPlaceholder')}
                      value={apiKeyInput}
                      onChange={(e) => setApiKeyInput(e.target.value)}
                    />
                  </div>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={handleClearApiKey}
                    title={t('configModal.openai.clearApiKey')}
                    disabled={!apiKeyInput.trim()}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('configModal.openai.apiKeyHint')}
                </p>
              </div>
            </div>

            {/* Developer Settings - Only shown in development mode */}
            {isDevelopment && (
              <>
                <Separator />
                <div className="space-y-3">
                  <div>
                    <h3 className="text-sm font-medium">{t('unifiedSettingsModal.general.developerTitle')}</h3>
                    <p className="text-xs text-muted-foreground">
                      {t('unifiedSettingsModal.general.developerDescription')}
                    </p>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label htmlFor="logging-toggle" className="flex items-center gap-2">
                        <Terminal className="h-4 w-4" />
                        <span>{t('configModal.developer.loggingLabel')}</span>
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        {t('configModal.developer.loggingDescription')}
                      </p>
                    </div>
                    <Switch
                      id="logging-toggle"
                      checked={loggingEnabled}
                      onCheckedChange={setLoggingEnabled}
                    />
                  </div>
                </div>
              </>
            )}
          </TabsContent>
          
          {/* View Tab */}
          <TabsContent value="view" className="space-y-6 py-4 flex-1 overflow-y-auto">
            {/* Layout Selection */}
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-medium">{t('sharePanel.layout.title')}</h3>
                <p className="text-xs text-muted-foreground">
                  {t('sharePanel.layout.description')}
                </p>
              </div>
              <RadioGroup
                value={shareViewOptions.layout}
                onValueChange={(value) => updateShareViewOption('layout', value as 'default' | 'embedded')}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="default" id="layout-default" />
                  <Label htmlFor="layout-default" className="flex-1 cursor-pointer">
                    <div>
                      <div className="font-medium">{t('sharePanel.layout.default')}</div>
                      <div className="text-xs text-muted-foreground">
                        {t('sharePanel.layout.defaultDescription')}
                      </div>
                    </div>
                  </Label>
                </div>
                {/* Legacy non-interactive option removed; embedded covers this use case */}
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="embedded" id="layout-embedded" />
                  <Label htmlFor="layout-embedded" className="flex-1 cursor-pointer">
                    <div>
                      <div className="font-medium">{t('sharePanel.layout.embedded')}</div>
                      <div className="text-xs text-muted-foreground">
                        {t('sharePanel.layout.embeddedDescription')}
                      </div>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <Separator />

            {/* UI Options */}
            <div className="space-y-3">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="funcControls" className="text-sm font-medium">
                      {t('sharePanel.options.funcControls')}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t('sharePanel.options.funcControlsDescription')}
                    </p>
                  </div>
                  <Switch
                    id="funcControls"
                    checked={shareViewOptions.funcControls}
                    onCheckedChange={(checked) => updateShareViewOption('funcControls', checked)}
                    disabled={shareViewOptions.layout === 'embedded'}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="tools" className="text-sm font-medium">
                      {t('sharePanel.options.tools')}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t('sharePanel.options.toolsDescription')}
                    </p>
                  </div>
                  <Switch
                    id="tools"
                    checked={shareViewOptions.tools}
                    onCheckedChange={(checked) => updateShareViewOption('tools', checked)}
                    disabled={shareViewOptions.layout === 'embedded'}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="header" className="text-sm font-medium">
                      {t('sharePanel.options.header')}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t('sharePanel.options.headerDescription')}
                    </p>
                  </div>
                  <Switch
                    id="header"
                    checked={shareViewOptions.header}
                    onCheckedChange={(checked) => updateShareViewOption('header', checked)}
                    disabled={false}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="zoom" className="text-sm font-medium">
                      {t('sharePanel.options.zoom')}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t('sharePanel.options.zoomDescription')}
                    </p>
                  </div>
                  <Switch
                    id="zoom"
                    checked={shareViewOptions.zoom}
                    onCheckedChange={(checked) => updateShareViewOption('zoom', checked)}
                    disabled={shareViewOptions.layout === 'embedded'}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="showUnits" className="text-sm font-medium">
                      {t('sharePanel.options.showUnits')}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t('sharePanel.options.showUnitsDescription')}
                    </p>
                  </div>
                  <Switch
                    id="showUnits"
                    checked={shareViewOptions.showUnits}
                    onCheckedChange={(checked) => updateShareViewOption('showUnits', checked)}
                    disabled={shareViewOptions.layout === 'embedded'}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="showPositiveQuadrant" className="text-sm font-medium">
                      {t('sharePanel.options.showPositiveQuadrant')}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t('sharePanel.options.showPositiveQuadrantDescription')}
                    </p>
                  </div>
                  <Switch
                    id="showPositiveQuadrant"
                    checked={shareViewOptions.showPositiveQuadrant ?? false}
                    onCheckedChange={(checked) => updateShareViewOption('showPositiveQuadrant', checked)}
                    disabled={false}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="fullscreen" className="text-sm font-medium">
                      {t('sharePanel.options.fullscreen')}
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      {t('sharePanel.options.fullscreenDescription')}
                    </p>
                  </div>
                  <Switch
                    id="fullscreen"
                    checked={shareViewOptions.fullscreen}
                    onCheckedChange={(checked) => updateShareViewOption('fullscreen', checked)}
                    disabled={shareViewOptions.layout === 'embedded'}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Reset to Defaults */}
            <div className="space-y-3">
              <Button onClick={handleResetViewOptions} size="sm" variant="ghost" className="w-full">
                <RefreshCw className="h-4 w-4 mr-1" />
                {t('unifiedSettingsModal.view.resetButton')}
              </Button>
            </div>
          </TabsContent>

          {/* Share Tab */}
          <TabsContent value="share" className="space-y-6 py-4 flex-1 overflow-y-auto">
            {/* Admin Controls & Same Origin Toggles (no section title) */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="admin-share" className="text-sm font-medium">
                    {t('unifiedSettingsModal.share.adminToggleLabel')}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {t('unifiedSettingsModal.share.adminToggleDescription')}
                  </p>
                </div>
                <Switch
                  id="admin-share"
                  checked={shareViewOptions.admin}
                  onCheckedChange={(checked) => updateShareViewOption('admin', checked)}
                  disabled={false}
                />
              </div>

              {/* Same Origin Toggle */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="same-origin-share" className="text-sm font-medium">
                    {t('sharePanel.options.sameOrigin')}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {t('sharePanel.options.sameOriginDescription')}
                  </p>
                </div>
                <Switch
                  id="same-origin-share"
                  checked={shareViewOptions.sameOrigin ?? true}
                  onCheckedChange={(checked) => updateShareViewOption('sameOrigin', checked)}
                  disabled={false}
                />
              </div>
            </div>

            <Separator />

            {/* Removed language selection: rely on Rails/browser default */}

            {/* Share URL */}
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-medium">{t('sharePanel.url.title')}</h3>
                <p className="text-xs text-muted-foreground">
                  {t('sharePanel.url.description')}
                </p>
              </div>
              <div className="flex gap-2">
                <Input
                  value={shareUrl}
                  readOnly
                  className="flex-1 text-xs"
                />
                <Button onClick={handleCopyUrl} size="sm" variant="outline">
                  <Copy className="h-4 w-4 mr-1" />
                  {t('sharePanel.url.copyButton')}
                </Button>
              </div>
            </div>

            <Separator />

            {/* Embed Code */}
            <div className="space-y-3">
              <div>
                <h3 className="text-sm font-medium">{t('sharePanel.embed.title')}</h3>
                <p className="text-xs text-muted-foreground">
                  {t('sharePanel.embed.description')}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="embed-width" className="text-xs">
                    {t('sharePanel.embed.widthLabel')} ({widthUnit})
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="embed-width"
                      type="number"
                      value={embedWidth}
                      onChange={(e) => setEmbedWidth(e.target.value)}
                      placeholder={widthUnit === '%' ? '100' : t('sharePanel.embed.widthPlaceholder')}
                      min={widthUnit === '%' ? '10' : '100'}
                      max={widthUnit === '%' ? '100' : '2000'}
                    />
                    <Select value={widthUnit} onValueChange={(v) => handleWidthUnitChange(v as 'px' | '%')}>
                      <SelectTrigger className="w-[90px]">
                        <SelectValue placeholder="%" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="%">%</SelectItem>
                        <SelectItem value="px">px</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="embed-height" className="text-xs">
                    {t('sharePanel.embed.heightLabel')} ({heightUnit})
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="embed-height"
                      type="number"
                      value={embedHeight}
                      onChange={(e) => setEmbedHeight(e.target.value)}
                      placeholder={heightUnit === '%' ? '100' : t('sharePanel.embed.heightPlaceholder')}
                      min={heightUnit === '%' ? '10' : '100'}
                      max={heightUnit === '%' ? '100' : '2000'}
                    />
                    <Select value={heightUnit} onValueChange={(v) => handleHeightUnitChange(v as 'px' | '%')}>
                      <SelectTrigger className="w-[90px]">
                        <SelectValue placeholder="px" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="px">px</SelectItem>
                        <SelectItem value="%">%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="embed-code" className="text-xs">
                  {t('sharePanel.embed.codeLabel')}
                </Label>
                <Textarea
                  id="embed-code"
                  value={embedCode}
                  readOnly
                  className="text-xs font-mono"
                  rows={3}
                />
              </div>
              <Button onClick={handleCopyEmbedCode} size="sm" variant="outline" className="w-full">
                <Copy className="h-4 w-4 mr-1" />
                {t('sharePanel.embed.copyButton')}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default UnifiedSettingsModal;